import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Button,
  Flex,
  Table,
  Badge,
  Tabs,
  Box,
  Text,
  Code,
  SegmentedControl,
} from "@radix-ui/themes";
import { atlasRoutes } from "@/app/router/routes";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import { Play } from "lucide-react";
import { AssistantActivityTimeline } from "@/pages/assistants/view/assistant-activity-timeline";
import { AssistantNoteReviewsTimeline } from "@/pages/assistants/view/assistant-note-reviews-timeline";
import { AssistantSlackMessagesTimeline } from "@/pages/assistants/view/assistant-slack-messages-timeline";

type ThreadStatus = "all" | "active" | "resolved" | "archived";
type TaskStatus = "all" | "pending" | "in_progress" | "completed" | "canceled";
type ReminderStatus = "all" | "pending" | "fired" | "completed" | "canceled";
type NotificationFilter = "all" | "unread" | "read";
type TabValue =
  | "timeline"
  | "threads"
  | "notifications"
  | "notes"
  | "tasks"
  | "reminders"
  | "slack";

export const AssistantsViewPage: FC = () => {
  const navigate = useNavigate();
  const { sessionId, tab } = useParams<{ sessionId: Uuid; tab?: string }>();
  const [statusFilter, setStatusFilter] = useState<ThreadStatus>("all");
  const [taskStatusFilter, setTaskStatusFilter] = useState<TaskStatus>("pending");
  const [reminderStatusFilter, setReminderStatusFilter] = useState<ReminderStatus>("pending");
  const [notificationFilter, setNotificationFilter] = useState<NotificationFilter>("unread");

  const resolveTab = (value: string | undefined): TabValue => {
    if (
      value === "threads" ||
      value === "notifications" ||
      value === "notes" ||
      value === "tasks" ||
      value === "reminders" ||
      value === "slack" ||
      value === "timeline"
    ) {
      return value;
    }
    return "timeline";
  };

  const activeTab = resolveTab(tab);

  const triggerAgentRun = useMutation({
    mutationFn: () =>
      api.post("/internal/v1/slack-assistant-sessions/{session_id}/trigger-self-review", {
        params: { path: { session_id: sessionId! } },
      }),
    onSuccess: result => {
      if (result.data?.success) {
        alert("Agent run triggered successfully!");
      } else {
        alert(`Failed: ${result.data?.message ?? "Unknown error"}`);
      }
    },
    onError: error => {
      alert(`Error: ${error.message}`);
    },
  });

  if (!sessionId) {
    return <div>Invalid Session ID</div>;
  }

  const temporalWorkflowQuery = encodeURIComponent(
    `\`WorkflowId\` STARTS_WITH "ProcessAssistantSessionWorkflow-${sessionId}"`
  ).replaceAll("%20", "+");
  const temporalWorkflowUrl = `https://cloud.temporal.io/namespaces/temporal-variant-solstice.rqp4d/workflows?query=${temporalWorkflowQuery}`;
  const logfireQuery = encodeURIComponent(
    `attributes->>'assistant_session_id' = '${sessionId}'`
  ).replaceAll("%20", "+");
  const logfireUrl = `https://logfire-us.pydantic.dev/mem/backend?q=${logfireQuery}&last=%226h%22`;

  const tabPath = (nextTab: TabValue) =>
    atlasRoutes.assistantsViewTab({
      params: { sessionId, tab: nextTab },
    }).path;

  useEffect(() => {
    if (!tab || tab !== activeTab) {
      navigate(tabPath(activeTab), { replace: true });
    }
  }, [tab, activeTab, navigate, sessionId]);

  return (
    <Container>
      <br />
      <Flex gap="3" align="center">
        <Button variant="ghost" onClick={() => navigate(atlasRoutes.assistants({}).path)}>
          ← Back to Assistants
        </Button>
      </Flex>

      <br />
      <Flex gap="3" align="center" justify="between">
        <div>
          <h1>Assistant Overview</h1>
          <Text size="2" color="gray">
            Session: <Code>{sessionId}</Code>
          </Text>
        </div>
        <Flex gap="2">
          <Button asChild variant="outline">
            <a href={temporalWorkflowUrl} target="_blank" rel="noreferrer">
              View Temporal Workflows →
            </a>
          </Button>
          <Button asChild variant="outline">
            <a href={logfireUrl} target="_blank" rel="noreferrer">
              View Logfire →
            </a>
          </Button>
          <Button
            variant="solid"
            color="green"
            onClick={() => triggerAgentRun.mutate()}
            disabled={triggerAgentRun.isPending}
          >
            <Play size={14} />
            {triggerAgentRun.isPending ? "Triggering..." : "Trigger Agent Run"}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(atlasRoutes.sessionsView({ params: { sessionId } }).path)}
          >
            View Session →
          </Button>
        </Flex>
      </Flex>

      <br />
      <br />

      <Tabs.Root value={activeTab} onValueChange={v => navigate(tabPath(v as TabValue))}>
        <Tabs.List>
          <Tabs.Trigger value="timeline">Timeline</Tabs.Trigger>
          <Tabs.Trigger value="threads">Threads</Tabs.Trigger>
          <Tabs.Trigger value="notifications">Notifications</Tabs.Trigger>
          <Tabs.Trigger value="notes">Notes</Tabs.Trigger>
          <Tabs.Trigger value="tasks">Tasks</Tabs.Trigger>
          <Tabs.Trigger value="reminders">Reminders</Tabs.Trigger>
          <Tabs.Trigger value="slack">Slack Messages</Tabs.Trigger>
        </Tabs.List>

        <Box pt="4">
          <Tabs.Content value="timeline">
            <AssistantActivityTimeline sessionId={sessionId} />
          </Tabs.Content>

          <Tabs.Content value="threads">
            <ThreadsTab
              sessionId={sessionId}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              navigate={navigate}
            />
          </Tabs.Content>

          <Tabs.Content value="notifications">
            <NotificationsTab
              sessionId={sessionId}
              filter={notificationFilter}
              setFilter={setNotificationFilter}
            />
          </Tabs.Content>

          <Tabs.Content value="notes">
            <NotesTab sessionId={sessionId} />
          </Tabs.Content>

          <Tabs.Content value="tasks">
            <TasksTab
              sessionId={sessionId}
              statusFilter={taskStatusFilter}
              setStatusFilter={setTaskStatusFilter}
            />
          </Tabs.Content>

          <Tabs.Content value="reminders">
            <RemindersTab
              sessionId={sessionId}
              statusFilter={reminderStatusFilter}
              setStatusFilter={setReminderStatusFilter}
            />
          </Tabs.Content>

          <Tabs.Content value="slack">
            <SlackTab sessionId={sessionId} />
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </Container>
  );
};

// Threads Tab Component
const ThreadsTab: FC<{
  sessionId: string;
  statusFilter: ThreadStatus;
  setStatusFilter: (status: ThreadStatus) => void;
  navigate: ReturnType<typeof useNavigate>;
}> = ({ sessionId, statusFilter, setStatusFilter, navigate }) => {
  const result = useQuery({
    queryKey: ["assistant-threads", sessionId, statusFilter],
    queryFn: () =>
      api.post("/internal/v1/assistant-threads/search", {
        body: {
          session_id: sessionId,
          limit: 100,
          offset: 0,
          filter_by_status: statusFilter === "all" ? undefined : statusFilter,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  if (result.isLoading) {
    return <Text>Loading threads...</Text>;
  }

  if (result.isError || !result.data?.data) {
    return <Text color="red">Error: {result.error?.message ?? "Unknown error"}</Text>;
  }

  const threads = result.data.data.results ?? [];

  const getStatusColor = (status: string): "green" | "orange" | "gray" | "blue" => {
    switch (status) {
      case "active":
        return "green";
      case "resolved":
        return "blue";
      case "archived":
        return "gray";
      default:
        return "orange";
    }
  };

  return (
    <>
      <Flex gap="3" align="center" mb="4">
        <Text size="2">Total threads: {result.data.data.total ?? 0}</Text>
        <SegmentedControl.Root
          value={statusFilter}
          onValueChange={value => setStatusFilter(value as ThreadStatus)}
          size="1"
        >
          <SegmentedControl.Item value="all">All</SegmentedControl.Item>
          <SegmentedControl.Item value="active">Active</SegmentedControl.Item>
          <SegmentedControl.Item value="resolved">Resolved</SegmentedControl.Item>
          <SegmentedControl.Item value="archived">Archived</SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      {threads.length === 0 ? (
        <Text color="gray">No threads found.</Text>
      ) : (
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Activities</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Tasks</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Reminders</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Last Activity</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {threads.map(thread => (
              <Table.Row
                key={thread.id}
                onClick={() =>
                  navigate(
                    atlasRoutes.assistantThreadsView({ params: { threadId: thread.id } }).path
                  )
                }
                style={{ cursor: "pointer" }}
              >
                <Table.Cell>
                  <TitleCell>{thread.title}</TitleCell>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={getStatusColor(thread.status)}>{thread.status}</Badge>
                </Table.Cell>
                <Table.Cell>{thread.activity_count}</Table.Cell>
                <Table.Cell>{thread.task_count}</Table.Cell>
                <Table.Cell>{thread.reminder_count}</Table.Cell>
                <Table.Cell>{DateTime.fromISO(thread.last_activity_at).toRelative()}</Table.Cell>
                <Table.Cell>
                  {DateTime.fromISO(thread.created_at).toLocaleString(DateTime.DATETIME_SHORT)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </>
  );
};

const NotificationsTab: FC<{
  sessionId: string;
  filter: NotificationFilter;
  setFilter: (filter: NotificationFilter) => void;
}> = ({ sessionId, filter, setFilter }) => {
  const result = useQuery({
    queryKey: ["assistant-notifications", sessionId, filter],
    queryFn: () =>
      api.post("/internal/v1/assistant-notifications/search", {
        body: {
          session_id: sessionId,
          limit: 200,
          offset: 0,
          unread_only: filter === "unread" ? true : filter === "read" ? false : undefined,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  if (result.isLoading) {
    return <Text>Loading notifications...</Text>;
  }

  if (result.isError || !result.data?.data) {
    return <Text color="red">Error: {result.error?.message ?? "Unknown error"}</Text>;
  }

  const notifications = result.data.data.results ?? [];
  // Filter client-side for "read" since backend only has unread_only
  const filteredNotifications =
    filter === "read"
      ? notifications.filter(n => n.read_at !== null)
      : filter === "unread"
        ? notifications.filter(n => n.read_at === null)
        : notifications;

  return (
    <>
      <Flex gap="3" align="center" mb="4">
        <Text size="2">Total: {result.data.data.total ?? 0}</Text>
        <SegmentedControl.Root
          value={filter}
          onValueChange={value => setFilter(value as NotificationFilter)}
          size="1"
        >
          <SegmentedControl.Item value="unread">Unread</SegmentedControl.Item>
          <SegmentedControl.Item value="read">Read</SegmentedControl.Item>
          <SegmentedControl.Item value="all">All</SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      {filteredNotifications.length === 0 ? (
        <Text color="gray">No notifications found.</Text>
      ) : (
        <Table.Root size="1">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Kind</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Read At</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Summary</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredNotifications.map(notification => (
              <Table.Row key={notification.id}>
                <Table.Cell>
                  <Badge variant="soft">{notification.kind}</Badge>
                </Table.Cell>
                <Table.Cell>
                  {notification.read_at ? (
                    <Badge color="gray">read</Badge>
                  ) : (
                    <Badge color="green">unread</Badge>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {notification.created_at
                    ? DateTime.fromISO(notification.created_at).toRelative()
                    : "-"}
                </Table.Cell>
                <Table.Cell>
                  {notification.read_at ? DateTime.fromISO(notification.read_at).toRelative() : "-"}
                </Table.Cell>
                <Table.Cell>
                  <Text size="1">{formatNotificationSummary(notification.payload)}</Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </>
  );
};

const NotesTab: FC<{ sessionId: string }> = ({ sessionId }) => {
  return <AssistantNoteReviewsTimeline sessionId={sessionId} />;
};

const TasksTab: FC<{
  sessionId: string;
  statusFilter: TaskStatus;
  setStatusFilter: (status: TaskStatus) => void;
}> = ({ sessionId, statusFilter, setStatusFilter }) => {
  const result = useQuery({
    queryKey: ["assistant-tasks", sessionId, statusFilter],
    queryFn: () =>
      api.post("/internal/v1/assistant-tasks/search", {
        body: {
          session_id: sessionId,
          limit: 200,
          offset: 0,
          status: statusFilter === "all" ? undefined : statusFilter,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  if (result.isLoading) {
    return <Text>Loading tasks...</Text>;
  }

  if (result.isError || !result.data?.data) {
    return <Text color="red">Error: {result.error?.message ?? "Unknown error"}</Text>;
  }

  const tasks = result.data.data.results ?? [];

  const getStatusColor = (status: string): "green" | "blue" | "yellow" | "gray" | "orange" => {
    switch (status) {
      case "completed":
        return "green";
      case "in_progress":
        return "blue";
      case "pending":
        return "yellow";
      case "canceled":
        return "gray";
      default:
        return "orange";
    }
  };

  return (
    <>
      <Flex gap="3" align="center" mb="4">
        <Text size="2">Total: {result.data.data.total ?? 0}</Text>
        <SegmentedControl.Root
          value={statusFilter}
          onValueChange={value => setStatusFilter(value as TaskStatus)}
          size="1"
        >
          <SegmentedControl.Item value="pending">Pending</SegmentedControl.Item>
          <SegmentedControl.Item value="in_progress">In Progress</SegmentedControl.Item>
          <SegmentedControl.Item value="completed">Completed</SegmentedControl.Item>
          <SegmentedControl.Item value="canceled">Canceled</SegmentedControl.Item>
          <SegmentedControl.Item value="all">All</SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      {tasks.length === 0 ? (
        <Text color="gray">No tasks found.</Text>
      ) : (
        <Table.Root size="1">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Task</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Thread</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Reminders</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Due</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tasks.map(task => (
              <Table.Row key={task.id}>
                <Table.Cell>
                  <Text weight="bold">{task.title}</Text>
                  {task.description && (
                    <Text size="1" color="gray">
                      {truncate(task.description, 80)}
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Badge color={getStatusColor(task.status)} variant="soft">
                    {task.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text size="1">{task.thread_title ?? "—"}</Text>
                </Table.Cell>
                <Table.Cell>
                  {task.reminders?.length ? (
                    <Text size="1">
                      {task.reminders
                        .slice(0, 3)
                        .map(reminder => reminder.content)
                        .join(" · ")}
                      {task.reminders.length > 3 && " …"}
                    </Text>
                  ) : (
                    <Text size="1" color="gray">
                      —
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {task.due_at
                    ? DateTime.fromISO(task.due_at).toLocaleString(DateTime.DATE_SHORT)
                    : "—"}
                </Table.Cell>
                <Table.Cell>{DateTime.fromISO(task.created_at).toRelative()}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </>
  );
};

const RemindersTab: FC<{
  sessionId: string;
  statusFilter: ReminderStatus;
  setStatusFilter: (status: ReminderStatus) => void;
}> = ({ sessionId, statusFilter, setStatusFilter }) => {
  const result = useQuery({
    queryKey: ["assistant-reminders", sessionId, statusFilter],
    queryFn: () =>
      api.post("/internal/v1/assistant-reminders/search", {
        body: {
          session_id: sessionId,
          limit: 200,
          offset: 0,
          status: statusFilter === "all" ? undefined : statusFilter,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  if (result.isLoading) {
    return <Text>Loading reminders...</Text>;
  }

  if (result.isError || !result.data?.data) {
    return <Text color="red">Error: {result.error?.message ?? "Unknown error"}</Text>;
  }

  const reminders = result.data.data.results ?? [];

  const getStatusColor = (status: string): "green" | "blue" | "yellow" | "gray" | "orange" => {
    switch (status) {
      case "completed":
        return "green";
      case "fired":
        return "blue";
      case "pending":
        return "yellow";
      case "canceled":
        return "gray";
      default:
        return "orange";
    }
  };

  return (
    <>
      <Flex gap="3" align="center" mb="4">
        <Text size="2">Total: {result.data.data.total ?? 0}</Text>
        <SegmentedControl.Root
          value={statusFilter}
          onValueChange={value => setStatusFilter(value as ReminderStatus)}
          size="1"
        >
          <SegmentedControl.Item value="pending">Pending</SegmentedControl.Item>
          <SegmentedControl.Item value="fired">Fired</SegmentedControl.Item>
          <SegmentedControl.Item value="completed">Completed</SegmentedControl.Item>
          <SegmentedControl.Item value="canceled">Canceled</SegmentedControl.Item>
          <SegmentedControl.Item value="all">All</SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      {reminders.length === 0 ? (
        <Text color="gray">No reminders found.</Text>
      ) : (
        <Table.Root size="1">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Reminder</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Task</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Thread</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Scheduled</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {reminders.map(reminder => (
              <Table.Row key={reminder.id}>
                <Table.Cell>
                  <Text>{reminder.content}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={getStatusColor(reminder.status)} variant="soft">
                    {reminder.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text size="1">{reminder.task_title ?? "—"}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="1">{reminder.thread_title ?? "—"}</Text>
                </Table.Cell>
                <Table.Cell>
                  {DateTime.fromISO(reminder.scheduled_for).toLocaleString(DateTime.DATETIME_SHORT)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </>
  );
};

const truncate = (value: string, maxLength: number) =>
  value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const formatNotificationSummary = (payload: Record<string, unknown> | null | undefined) => {
  if (!payload) {
    return "—";
  }
  const notificationKind =
    typeof payload.notification_kind === "string" ? payload.notification_kind : undefined;
  if (!notificationKind) {
    return truncate(JSON.stringify(payload), 160);
  }

  const parts: string[] = [notificationKind];
  switch (notificationKind) {
    case "USER_MESSAGE": {
      if (typeof payload.source === "string") {
        parts.push(payload.source);
      }
      if (typeof payload.text === "string" && payload.text.trim() !== "") {
        parts.push(`"${truncate(payload.text.trim(), 80)}"`);
      }
      break;
    }
    case "NOTE_EDIT_WINDOW_CREATED": {
      const editCount = coerceNumber(payload.edit_count);
      if (typeof editCount === "number") {
        parts.push(`${editCount} edits`);
      }
      if (typeof payload.note_id === "string") {
        parts.push(`note ${payload.note_id}`);
      }
      break;
    }
    case "NOTES_REVIEW_REQUESTED": {
      const noteCount = coerceNumber(payload.note_count);
      if (typeof noteCount === "number") {
        parts.push(`${noteCount} notes`);
      }
      if (typeof payload.timezone === "string") {
        parts.push(payload.timezone);
      }
      break;
    }
    case "ASSISTANT_OBJECTIVE_DUE":
    case "ASSISTANT_SELF_REVIEW_DUE": {
      if (typeof payload.reason === "string") {
        parts.push(truncate(payload.reason, 120));
      }
      break;
    }
    case "SCHEDULED_REMINDER_DUE": {
      if (typeof payload.content === "string") {
        parts.push(truncate(payload.content, 120));
      }
      break;
    }
    case "MANUAL_TRIGGER": {
      if (typeof payload.source === "string") {
        parts.push(payload.source);
      }
      if (typeof payload.reason === "string") {
        parts.push(payload.reason);
      }
      break;
    }
    case "SLACK_REACTION_ADDED": {
      if (typeof payload.emoji === "string") {
        parts.push(`:${payload.emoji}:`);
      }
      break;
    }
    default:
      break;
  }

  return parts.join(" | ");
};

// Slack Tab Component
const SlackTab: FC<{ sessionId: string }> = ({ sessionId }) => {
  return <AssistantSlackMessagesTimeline sessionId={sessionId} />;
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;

const TitleCell = styled.div`
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
